import { catchError, switchMap, takeWhile } from 'rxjs/operators';
import { of, Subscription, timer } from 'rxjs';
import { RecommendationModel } from 'projects/core/src/lib/models/recommendation.model';
import { RatingtypeModel } from './../../../../../../core/src/lib/models/ratingtype.model';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehavioranchorModel } from 'projects/core/src/lib/models/behavioranchor.model';
import { CompetenceModel } from 'projects/core/src/lib/models/competence.model';
import { ObserverModel } from 'projects/core/src/lib/models/observer.model';
import { AcModel } from '../../../models/ac.model';
import { SlotModel } from '../../../models/slot.model';
import { DataService } from '../../../services/data.service';
import {
  faUser,
  faCheckSquare,
  faSquare
} from '@fortawesome/pro-light-svg-icons';
import {
  faSigma
} from '@fortawesome/pro-solid-svg-icons';
import { ApiService } from '../../../services/api.service';
import { UiService } from '../../../services/ui.service';
import { CandidateModel } from '../../../models/candidate.model';
import { AuthStateService } from '../../../services/auth-state.service';
import { ExercisepartModel } from '../../../models/exercisepart.model';
import { RunnerOnlinetestModel } from '../../../models/runner-onlinetest.model';
import { TranslocoService } from '@ngneat/transloco';
import { AppService } from '../../../services/app.service';
import { HelperService } from 'projects/admin/src/app/services/helper.service';
import { OnlinetestModel } from 'projects/core/src/lib/models/onlinetest.model';

const POLLING_INTERVAL = 5;

@Component({
  selector: 'app-conference',
  templateUrl: './conference.component.html',
  styleUrls: ['./conference.component.scss'],
})
export class ConferenceComponent implements OnInit, OnDestroy {

  @ViewChild('tablecontainer') tablecontainer: ElementRef<HTMLDivElement>;

  private polling: Subscription = null;

  public loading = true;
  public ac: AcModel = null;
  public candidate: CandidateModel = null;
  public conference: SlotModel = null;
  public exerciseparts: Array<any> = [];
  public onlinetests: Array<RunnerOnlinetestModel> = [];
  public competences: Array<CompetenceModel> = [];
  public ratingData: Array<any> = [];
  public ratings: Array<any> = [];
  public observers: Array<ObserverModel> = [];
  public ratingtype: RatingtypeModel = null;
  public recommendations: Array<RecommendationModel> = [];
  public recommendation: RecommendationModel = null;
  public language: string = null;
  public valueWidth = '30px';
  public stepWidth = 0.5;

  private acSubscription: Subscription = null;
  private conferenceSubscription: Subscription = null;
  private networkErrorSubscription: Subscription = null;
  private networkError = false;

  public faUser = faUser;
  public faSigma = faSigma;
  public faCheckSquare = faCheckSquare;
  public faSquare = faSquare;

  constructor(
    private data: DataService,
    private api: ApiService,
    private ui: UiService,
    private app: AppService,
    private helper: HelperService,
    public auth: AuthStateService,
    public transloco: TranslocoService
  ) {}

  ngOnInit(): void {
    this.ui.setPageTitle(this.transloco.translate('common.labels.conference'));
    this.language = this.transloco.getActiveLang();
    this.acSubscription = this.data.ac$.subscribe({
      next: (ac: AcModel) => {
        if (ac !== null) {
          this.ac = ac;
          this.ratingtype = this.ac.ratingtype;
          this.stepWidth = Math.min(this.stepWidth, this.ratingtype.step);
          this.conference = this.ac.getConference();
          this.data.setActiveNav('conference');
          if (this.conference?.isRunningOrPaused('conference') || this.auth.isModerator()) {
            this.observers = this.ac.observers.filter((observer: ObserverModel) => {
              return observer.type === 'active';
            });
            this.mapRecommendations();
            this.api.getConferenceData().subscribe({
              next: (result: any) => {
                this.ratingData = result.data;
                this.initData();
                this.changeCandidate();
                this.initPolling();
                this.calcColumnWidths();
                this.loading = false;
              }
            });
          } else {
            this.app.redirectToDashboard();
          }
        }
      }
    });
    this.networkErrorSubscription = this.data.networkError$.subscribe((networkError: boolean) => {
      this.networkError = networkError;
    });
    this.transloco.langChanges$.subscribe({
      next: () => {
        this.language = this.transloco.getActiveLang();
        this.mapRecommendations();
      }
    });
    // subscribe to ratings
    this.conferenceSubscription = this.api.conference$.subscribe({
      next: () => {},
      error: () => {
        this.ui.toastError(this.transloco.translate('conference.messages.value_not_saved'));
      }
    });
  }

  ngOnDestroy(): void {
    if (this.polling !== null) {
      this.polling.unsubscribe();
      this.polling = null;
    }
    if (this.acSubscription !== null) {
      this.acSubscription.unsubscribe();
      this.acSubscription = null;
    }
    if (this.conferenceSubscription !== null) {
      this.conferenceSubscription.unsubscribe();
      this.conferenceSubscription = null;
    }
    if (this.networkErrorSubscription !== null) {
      this.networkErrorSubscription.unsubscribe();
      this.networkErrorSubscription = null;
    }
  }

  public changeCandidate(candidate: CandidateModel = null): void {
    this.candidate = candidate || this.ac?.candidates[0] || null;
    this.setCandidateData();
  }

  public isActive(): boolean {
    return this.auth.isModerator() && this.conference?.isRunning('conference') && this.networkError !== true;
  }

  public saveResult(): void {
    if (this.isActive() && this.auth.isModerator()) {
      // set data from current candidate
      this.ratingData = this.ratingData.map((entry: any) => {
        if (entry.candidate === this.candidate?.hashed_id) {
          this.candidate.recommendation = this.recommendation?.id || null;
          const candidatePayload: any = {
            recommendation: this.recommendation,
            values: {}
          };
          this.competences.forEach((competence: CompetenceModel) => {
            candidatePayload.values[competence.id] = {
              value: competence.value,
              name: competence.name,
            };
          });
          entry.result = candidatePayload;
        }
        return entry;
      });
      // build payload
      const payload: any = {};
      this.ratingData.forEach((entry: any) => {
        payload[entry.candidate] = entry.result;
      });
      this.api.setConferenceData(payload);
    }
  }

  public exportToExcel(): void {
    import('xlsx').then(xlsx => {
      const workbook = {
        Sheets: {},
        SheetNames: []
      };
      const actualCandidate = this.candidate;
      this.ac?.candidates.forEach((candidate: CandidateModel) => {
        this.changeCandidate(candidate);
        const worksheet = xlsx.utils.table_to_sheet(this.getExcelData());
        workbook.Sheets[candidate.fullname] = worksheet;
        workbook.SheetNames.push(candidate.fullname);
      });
      this.changeCandidate(actualCandidate);
      const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
      this.helper.saveAsExcelFile(excelBuffer, 'conference_data');
    });
  }

  private getExcelData(): any {
    const table: HTMLElement = document.createElement('table');
    const rowHeader: HTMLElement = document.createElement('tr');

    // competence header cell
    const cell: HTMLElement = document.createElement('td');
    cell.innerHTML = this.transloco.translate('common.labels.competence');
    rowHeader.appendChild(cell);

    // onlinetest header cells
    this.onlinetests.forEach((onlinetest: RunnerOnlinetestModel) => {
      const cell: HTMLElement = document.createElement('td');
      cell.innerHTML = onlinetest.fullname;
      if (onlinetest.with_observers === true) {
        cell.setAttribute('colspan', (this.observers.length + 2).toString());
      }
      rowHeader.appendChild(cell);
    });

    // excerciseparts header cells
    this.exerciseparts.forEach((exercisepart: any) => {
      const cell: HTMLElement = document.createElement('td');
      cell.innerText = exercisepart.name + ' ' + exercisepart.partName;
      cell.setAttribute('colspan', (this.observers.length + 2).toString());
      rowHeader.appendChild(cell);
    });

    // total header cell
    const cellTotal: HTMLElement = document.createElement('td');
    cellTotal.innerHTML = this.transloco.translate('common.labels.total');
    rowHeader.appendChild(cellTotal);

    // conference header cell
    const cellConference: HTMLElement = document.createElement('td');
    cellConference.innerHTML = this.transloco.translate('common.labels.conference');
    rowHeader.appendChild(cellConference);

    table.appendChild(rowHeader);

    // observer row
    const rowObserver: HTMLElement = document.createElement('tr');
    rowObserver.appendChild(document.createElement('td'));
    this.onlinetests.forEach((onlinetest: RunnerOnlinetestModel) => {

      const cellModerator: HTMLElement = document.createElement('td');
      cellModerator.innerText = this.ac.moderator.fullname;
      rowObserver.appendChild(cellModerator);

      if (onlinetest.with_observers === true) {
        this.observers.forEach((observer: ObserverModel) => {
          const cell: HTMLElement = document.createElement('td');
          cell.innerText = observer.fullname;
          rowObserver.appendChild(cell);
        });
        const cellSum: HTMLElement = document.createElement('td');
        cellSum.innerText = this.transloco.translate('common.labels.sum');
        rowObserver.appendChild(cellSum);
      }

    });

    this.exerciseparts.forEach(() => {
      const cellModerator: HTMLElement = document.createElement('td');
      cellModerator.innerText = this.ac.moderator.fullname;
      rowObserver.appendChild(cellModerator);

      this.observers.forEach((observer: ObserverModel) => {
        const cell: HTMLElement = document.createElement('td');
        cell.innerText = observer.fullname;
        rowObserver.appendChild(cell);
      });

      const cellSum: HTMLElement = document.createElement('td');
      cellSum.innerText = this.transloco.translate('common.labels.sum');
      rowObserver.appendChild(cellSum);
    });

    table.appendChild(rowObserver);

    this.competences.forEach((competence: CompetenceModel) => {
      const row: HTMLElement = document.createElement('tr');

      // competency name
      const cellCompetency: HTMLElement = document.createElement('td');
      cellCompetency.innerHTML = competence.name[this.language.toUpperCase()];
      row.appendChild(cellCompetency);

      // onlinetests
      this.onlinetests.forEach((onlinetest: RunnerOnlinetestModel) => {

        const cellModerator: HTMLElement = document.createElement('td');
        const sumModerator = this.getSingleValueOnlinetest(competence.id, onlinetest.onlinetest_id, this.ac.moderator.hashed_id);
        cellModerator.innerHTML = this.formatCellNumber(sumModerator, 1);
        row.appendChild(cellModerator);

        if (onlinetest.with_observers === true) {
          this.observers.forEach((observer: ObserverModel) => {
            const cell: HTMLElement = document.createElement('td');
            const sum = this.getSingleValueOnlinetest(competence.id, onlinetest.onlinetest_id, observer.hashed_id);
            cell.innerHTML = this.formatCellNumber(sum, 1);
            row.appendChild(cell);
          });

          const cellSum: HTMLElement = document.createElement('td');
          const sum: number = this.getSingleSumOnlinetest(competence.id, onlinetest.onlinetest_id);
          cellSum.innerHTML = this.formatCellNumber(sum, 1);
          row.appendChild(cellSum);
        }

      });

      // excercise parts
      this.exerciseparts.forEach((exercisepart: ExercisepartModel) => {
        const cellModerator: HTMLElement = document.createElement('td');
        const sumModerator = this.getSingleValue(competence.id, exercisepart.id, this.ac.moderator.hashed_id);
        cellModerator.innerHTML = this.formatCellNumber(sumModerator, 1);
        row.appendChild(cellModerator);

        this.observers.forEach((observer: ObserverModel) => {
          const cell: HTMLElement = document.createElement('td');
          const sum = this.getSingleValue(competence.id, exercisepart.id, observer.hashed_id);
          cell.innerHTML = this.formatCellNumber(sum, 1);
          row.appendChild(cell);
        });

        const cellSum: HTMLElement = document.createElement('td');
        const sum: number = this.getSingleSum(competence.id, exercisepart.id);
        cellSum.innerHTML = this.formatCellNumber(sum, 1);
        row.appendChild(cellSum);
      });

      const cellCompetencySum: HTMLElement = document.createElement('td');
      const competencySum = this.getCompetenceSum(competence.id);
      cellCompetencySum.innerHTML = this.formatCellNumber(competencySum, 2);
      row.appendChild(cellCompetencySum);

      const cellCompetencyResult: HTMLElement = document.createElement('td');
      cellCompetencyResult.innerHTML = this.formatCellNumber(competence.value, 1);
      row.appendChild(cellCompetencyResult);

      table.appendChild(row);
    });

    const rowTotal: HTMLElement = document.createElement('tr');

    const cellTotalText: HTMLElement = document.createElement('td');
    cellTotalText.innerHTML = this.transloco.translate('common.labels.total');
    rowTotal.appendChild(cellTotalText);

    // onlinetests
    this.onlinetests.forEach((onlinetest: RunnerOnlinetestModel) => {

      const cellModerator: HTMLElement = document.createElement('td');
      const sumModerator = this.getUserSumOnlinetest(onlinetest.onlinetest_id, this.ac.moderator.hashed_id);
      cellModerator.innerHTML = this.formatCellNumber(sumModerator, 1);
      rowTotal.appendChild(cellModerator);

      if (onlinetest.with_observers === true) {
        this.observers.forEach((observer: ObserverModel) => {
          const cell: HTMLElement = document.createElement('td');
          const sumObserver = this.getUserSumOnlinetest(onlinetest.onlinetest_id, observer.hashed_id);
          cell.innerHTML = this.formatCellNumber(sumObserver, 1);
          rowTotal.appendChild(cell);
        });

        const cellSum: HTMLElement = document.createElement('td');
        const sumExcerisePart: number = this.getOnlinetestSum(onlinetest.onlinetest_id);
        cellSum.innerHTML = this.formatCellNumber(sumExcerisePart, 1);
        rowTotal.appendChild(cellSum);
      }

    });

    // excercise parts
    this.exerciseparts.forEach((exercisepart: ExercisepartModel) => {
      const cellModerator: HTMLElement = document.createElement('td');
      const sumModerator = this.getUserSum(exercisepart.id, this.ac.moderator.hashed_id);
      cellModerator.innerHTML = this.formatCellNumber(sumModerator, 1);
      rowTotal.appendChild(cellModerator);

      this.observers.forEach((observer: ObserverModel) => {
        const cell: HTMLElement = document.createElement('td');
        const sumObserver = this.getUserSum(exercisepart.id, observer.hashed_id);
        cell.innerHTML = this.formatCellNumber(sumObserver, 1);
        rowTotal.appendChild(cell);
      });

      const cellSum: HTMLElement = document.createElement('td');
      const sumExcerisePart: number = this.getExercisepartSum(exercisepart.id);
      cellSum.innerHTML = this.formatCellNumber(sumExcerisePart, 1);
      rowTotal.appendChild(cellSum);
    });

    const cellCompetencySum: HTMLElement = document.createElement('td');
    const competencySum = this.getMainSum();
    cellCompetencySum.innerHTML = this.formatCellNumber(competencySum, 2);
    rowTotal.appendChild(cellCompetencySum);

    const cellCompetencyResult: HTMLElement = document.createElement('td');
    const conferenceSum = this.getMainConferenceSum();
    cellCompetencyResult.innerHTML = this.formatCellNumber(conferenceSum, 1);
    rowTotal.appendChild(cellCompetencyResult);

    table.appendChild(rowTotal);

    return table;
  }


  private formatCellNumber(value: number, digits: number): string {
    return value <= 0 ? '-' : value.toFixed(digits);
  }


  private initData(): void {
    const competenceIds: Array<number> = [];
    this.exerciseparts = [];
    this.onlinetests = this.ac.onlinetests || [];
    this.ac.slots?.forEach((slot: SlotModel) => {
      if (slot.isRateable() && slot.behavioranchors?.length > 0) {
        this.exerciseparts.push({
          id: slot.exercisepart_id,
          name: slot.exercisename,
          partName: slot.name,
        });
        slot.behavioranchors.forEach((behavioranchor: BehavioranchorModel) => {
          competenceIds.push(behavioranchor.competence_id);
        });
      }
    });
    this.competences = [...this.ac?.competences].filter((competence: CompetenceModel) => {
      return competenceIds.includes(competence.id);
    });
    this.ac.onlinetests?.forEach((onlinetest: OnlinetestModel) => {
      onlinetest.competences?.forEach((otCompetence: CompetenceModel) => {
        if (this.competences?.find((actual) => actual.id === otCompetence.id) === undefined) {
          this.competences.push(otCompetence);
        }
      });
    });
    this.competences.sort((a: any, b: any) => (a.position > b.position ? 1 : b.position > a.position ? -1 : 0));
  }

  private setCandidateData(): void {
    const data: any = this.ratingData.find(entry => entry.candidate === this.candidate?.hashed_id) || null;
    if (data !== null) {
      this.ratings = data.data;
      this.recommendation =
        this.recommendations.find((recommendation: RecommendationModel) => {
          return recommendation.id === parseInt(data.result?.recommendation?.id, 10);
        }) || null;
      this.competences.forEach((competence: CompetenceModel) => {
        if (data.result?.values && data.result?.values[competence.id]) {
          competence.value = data.result?.values[competence.id]?.value;
        } else {
          this.setCompetenceInitValue(competence);
        }
      });
    }
  }

  private initPolling(): void {
    if (this.polling === null) {
      this.polling = timer(0, POLLING_INTERVAL * 1000)
        .pipe(
          takeWhile(() => this.auth.isObserver()),
          switchMap(() => {
            if (this.conference?.isRunning('conference')) {
              return this.api.getConferenceData().pipe(
                catchError(() => {
                  return of(null);
                })
              );
            } else {
              return of(null);
            }
          })
        )
        .subscribe({
          next: (result: any) => {
            if (result !== null) {
              this.ratingData = result?.data;
              this.setCandidateData();
            }
          }
        });
    }
  }

  private isCompetenceActive(id: number): boolean {
    const competence = this.competences.find((actual: CompetenceModel) => actual.id === id);
    return competence !== undefined ? competence.value >= 0 : false;
  }

  private setCompetenceInitValue(competence: CompetenceModel): void {
    competence.value = -1;
  }

  private calcColumnWidths(): void {
    const MAX_WIDTH = 50;
    let cols = 0;
    cols += this.onlinetests.length;
    cols += this.exerciseparts.length * (this.observers.length + 2); // (2 = moderator + total col)
    this.valueWidth = (MAX_WIDTH / cols).toString() + '%';
  }

  private mapRecommendations(): void {
    this.recommendations = this.ac?.recommendations.map((recommendation: RecommendationModel): any => {
      return {
        id: recommendation.id,
        name: recommendation.getLang('name', this.language),
      };
    });
  }

  public getSingleValue(competence: number, exercisepart: number, rater: string): number {
    let value = 0;
    let count = 0;
    this.ratings.forEach((rating: any) => {
      if (competence === rating.competence_id && exercisepart === rating.exercisepart_id && rater === rating.rater_id) {
        value = rating.value;
        count++;
      }
    });
    return count > 0 ? value : -1;
  }

  public getSingleValueOnlinetest(competence: number, onlinetest: number, rater: string): number {
    let value = 0;
    let count = 0;
    this.ratings.forEach((rating: any) => {
      if (competence === rating.competence_id && onlinetest === rating.onlinetest_id && rater === rating.rater_id) {
        value = rating.value;
        count++;
      }
    });
    return count > 0 ? value : -1;
  }

  public getSingleSum(competence: number, exercisepart: number): number {
    let value = 0;
    let count = 0;
    this.ratings.forEach((rating: any) => {
      if (competence === rating.competence_id && exercisepart === rating.exercisepart_id) {
        value += rating.value;
        count++;
      }
    });
    return count > 0 ? value / count : -1;
  }

  public getSingleSumOnlinetest(competence: number, onlinetest: number): number {
    let value = 0;
    let count = 0;
    this.ratings.forEach((rating: any) => {
      if (competence === rating.competence_id && onlinetest === rating.onlinetest_id) {
        value += rating.value;
        count++;
      }
    });
    return count > 0 ? value / count : -1;
  }

  public getUserSum(exercisepart: number, rater: string): number {
    let value = 0;
    let count = 0;
    this.ratings.forEach((rating: any) => {
      if (exercisepart === rating.exercisepart_id && rater === rating.rater_id && this.isCompetenceActive(rating.competence_id)) {
        value += rating.value;
        count++;
      }
    });
    return count > 0 ? value / count : -1;
  }

  public getUserSumOnlinetest(onlinetest: number, rater: string): number {
    let value = 0;
    let count = 0;
    this.ratings.forEach((rating: any) => {
      if (onlinetest === rating.onlinetest_id && rater === rating.rater_id && this.isCompetenceActive(rating.competence_id)) {
        value += rating.value;
        count++;
      }
    });
    return count > 0 ? value / count : -1;
  }

  public getOnlinetestSum(onlinetest: number): number {
    let value = 0;
    let count = 0;
    this.ratings.forEach((rating: any) => {
      if (onlinetest === rating.onlinetest_id && this.isCompetenceActive(rating.competence_id)) {
        value += rating.value;
        count++;
      }
    });
    return count > 0 ? value / count : -1;
  }

  public getCompetenceSum(competence: number): number {
    let value = 0;
    let count = 0;
    // if (this.isCompetenceActive(competence)) {
    this.exerciseparts.forEach((exercisepart: ExercisepartModel) => {
      const sum = this.getSingleSum(competence, exercisepart.id);
      if (sum >= 0) {
        value += sum;
        count++;
      }
    });
    this.onlinetests.forEach((onlinetest: RunnerOnlinetestModel) => {
      const sum = this.getSingleSumOnlinetest(competence, onlinetest.onlinetest_id);
      if (sum >= 0) {
        value += sum;
        count++;
      }
    });
    // }
    return count > 0 ? value / count : -1;
  }

  public getExercisepartSum(exercisepart: number): number {
    let value = 0;
    let count = 0;
    this.ratings.forEach((rating: any) => {
      if (exercisepart === rating.exercisepart_id && this.isCompetenceActive(rating.competence_id)) {
        value += rating.value;
        count++;
      }
    });
    return count > 0 ? value / count : -1;
  }

  public getMainSum(): number {
    let value = 0;
    let count = 0;
    this.competences.forEach((competence: CompetenceModel) => {
      const sum = this.getCompetenceSum(competence.id);
      if (this.isCompetenceActive(competence.id) && sum >= 0) {
        value += sum;
        count++;
      }
    });
    return count > 0 ? value / count : -1;
  }

  public getMainConferenceSum(): number {
    let value = 0;
    let count = 0;
    this.competences.forEach((competence: CompetenceModel) => {
      if (competence.value >= 0) {
        value += competence.value;
        count++;
      }
    });
    return count > 0 ? value / count : -1;
  }

}
